import {
  AppProvider,
  Frame,
  Navigation,
  TopBar,
  Loading,
} from "@shopify/polaris";
import Error from "./Error";
import Login from "./Access/Login";
import {
  BrowserRouter,
  Switch,
  Route,
  Link as ReactRouterLink,
  useLocation,
  useHistory,
} from "react-router-dom";
import { QueryParamProvider } from 'use-query-params';
import { keycloak, ProvideAuth, useAuth } from "./Access/Auth.hook";
import Home from "./Home/Home";
import Products from "./Products/Products";
import Customers from "./Customers/Customers";
import {
  CustomersMajor,
  HomeMajor,
  OnlineStoreMajor,
  OrdersMajor,
  PackageMajor,
  ProductsMajor,
  SettingsMajor,
  ShipmentMajor,
} from "@shopify/polaris-icons";
import translations from "@shopify/polaris/locales/en.json";
import { useCallback, useState } from "react";
import { ApolloProvider, ApolloClient, InMemoryCache } from "@apollo/client";
import { BatchHttpLink } from "apollo-link-batch-http";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import NewCustomer from "./Customers/New";
import Customer from "./Customers/Customer";
import Product from "./Products/Product";
import Collections from "./Collection/Collections";
import Collection from "./Collection/Collection";
import Inventory from "./Inventory/Inventory";
import Orders from "./Orders/Orders";
import Order from "./Orders/Order";
import Settings from "./Settings/Settings";
import Locations from "./Settings/Locations";
import Location from "./Settings/Location";
import Metas from "./Settings/Metas";
import Meta from "./Settings/Meta";
import { Helmet } from "react-helmet";
import Payments from "./Settings/Payments";
import Payment from "./Settings/Payment";
import Shippers from "./Settings/Shippers";
import Shipper from "./Settings/Shipper";
import Channels from "./Settings/Channels";
import Channel from "./Settings/Channel";

const uri = process.env.REACT_APP_URI || "https://graph.brasal.ma";
// const uri = "http://localhost:3001";

const httpLink = new BatchHttpLink({
  uri,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = JSON.parse(localStorage.getItem("token"))?.token;
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

export const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    console.log('errorLink', graphQLErrors, networkError);
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        if (err.message.includes("Unauthorized")) {
          keycloak.login();
          return;
        }
      }
    }
    return forward(operation);
  }
);

const possibleTypes = {"AddressEvent":["AddressCreateEvent","AddressUpdateEvent"],"CustomerEvent":["AddressCreateEvent","AddressUpdateEvent","CustomerCreateEvent","CustomerUpdateEvent","OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"CustomerEventUnion":["AddressCreateEvent","AddressUpdateEvent","CustomerCreateEvent","CustomerUpdateEvent","OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"Entity":["AccessToken","Address","AlternateContact","Brand","Channel","City","Collection","Customer","Discount","File","Fulfillment","FulfillmentQueue","Image","InventoryAdjustment","InventoryItem","InventoryLevel","Location","Order","Pack","Payment","PaymentProvider","Shipper","SingleVariantProduct","Transaction","User"],"Event":["AddressCreateEvent","AddressUpdateEvent","CustomerCreateEvent","CustomerUpdateEvent","OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"EventUnion":["AddressCreateEvent","AddressUpdateEvent","CustomerCreateEvent","CustomerUpdateEvent","OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"Mediable":["Pack","SingleVariantProduct"],"Metable":["Boutique","Brand","Channel","City","Collection","Customer","Discount","Fulfillment","FulfillmentQueue","Image","Order","Pack","Payment","Shipper","SingleVariantProduct","Transaction","User"],"OrderEvent":["OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"OrderEventUnion":["OrderCreateEvent","OrderUpdateEvent","PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"PaymentEvent":["PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"PaymentEventUnion":["PaymentCreateEvent","PaymentUpdateEvent","TransactionCreateEvent","TransactionUpdateEvent"],"Person":["Address","AlternateContact","Customer","User"],"Product":["Pack","SingleVariantProduct"],"ProductUnion":["Pack","SingleVariantProduct"],"Referable":["Address","AlternateContact","Boutique","Brand","Collection","Customer","Discount","FulfillmentQueue","Order","Pack","Payment","SingleVariantProduct"],"Taggable":["Customer","Discount","Order","Pack","SingleVariantProduct"],"TransactionEvent":["TransactionCreateEvent","TransactionUpdateEvent"],"UpdateEvent":["AddressUpdateEvent","CustomerUpdateEvent","OrderUpdateEvent","PaymentUpdateEvent","TransactionUpdateEvent"]};

// fetch(uri, {
//   method: 'POST',
//   headers: { 'Content-Type': 'application/json' },
//   body: JSON.stringify({
//     variables: {},
//     query: `
//       {
//         __schema {
//           types {
//             kind
//             name
//             possibleTypes {
//               name
//             }
//           }
//         }
//       }
//     `,
//   }),
// }).then(result => result.json())
// .then(result => {
//   result.data.__schema.types.forEach(supertype => {
//     if (supertype.possibleTypes) {
//       possibleTypes[supertype.name] =
//         supertype.possibleTypes.map(subtype => subtype.name);
//     }
//   });
//   console.log(possibleTypes)
// });

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache({ possibleTypes }),
  defaultOptions: {
    query: {
      fetchPolicy: "cache-first",
    },
    mutate: {
      fetchPolicy: "no-cache",
    },
    watchQuery: {
      fetchPolicy: "network-only",
    },
  },
});

const IS_EXTERNAL_LINK_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/;

function Link({ children, url = "", external, ref, ...rest }) {
  // react-router only supports links to pages it can handle itself. It does not
  // support arbirary links, so anything that is not a path-based link should
  // use a reglar old `a` tag
  if (external || IS_EXTERNAL_LINK_REGEX.test(url)) {
    rest.target = "_blank";
    rest.rel = "noopener noreferrer";
    return (
      <a href={url} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <ReactRouterLink to={url} {...rest}>
      {children}
    </ReactRouterLink>
  );
}

function PrivateRoute({ children, ...rest }) {
  const auth = useAuth();
  return (
    <Route
      {...rest}
      render={({ location }) =>
        auth.accessToken ? (
          children
        ) : (
          <Frame>
            <Loading />
          </Frame>
        )
      }
    />
  );
}

function RoleRoute({ roles, ...rest }) {
  const auth = useAuth();
  if (auth.user.roles.some(role => roles.includes(role))) {
    return (
      <Route {...rest} />
    )
  } else {
    return (<Error title="Not allowed" />)
  }
}

function filterMenu(menu, roles) {
  return menu.filter(
    menu => {
      if (menu.subNavigationItems) {
        menu.subNavigationItems = menu.subNavigationItems.filter(
          subMenu => {
            if (subMenu.roles) {
              return subMenu.roles.some(
                role => roles.includes(role)
              )
            }
            return true
          }
        )
        return !!menu.subNavigationItems.length
      } else if (menu.roles) {
        return menu.roles.some(
          role => roles.includes(role)
        )
      }
      return true
    }
  )
}

function AppFrame() {
  const location = useLocation();
  const history = useHistory();
  const auth = useAuth();
  const [mobileNavigationActive, setMobileNavigationActive] = useState(false);
  const toggleMobileNavigationActive = useCallback(
    () =>
      setMobileNavigationActive(
        (mobileNavigationActive) => !mobileNavigationActive
      ),
    []
  );

  const [userMenuOpen, setUserMenuOpen] = useState(false)

  history.listen(
    () => {
      setMobileNavigationActive(false)
    }
  )

  if (!auth.user) {
    return (
      <Frame>
        <Loading />
      </Frame>
    );
  }

  return (
    <Frame
      topBar={
        <TopBar
          showNavigationToggle
          onNavigationToggle={toggleMobileNavigationActive}
          userMenu={
            <TopBar.UserMenu
              actions={[{
                items: [{
                  content: 'Log out',
                  onAction: () => auth.logout()
                }]
              }]}
              name={auth.user.name}
              detail={'brasal'}
              // initials="D"
              open={userMenuOpen}
              onToggle={() => setUserMenuOpen(!userMenuOpen)}
            />
          }
        />
      }
      showMobileNavigation={mobileNavigationActive}
      onNavigationDismiss={toggleMobileNavigationActive}
      navigation={
        <Navigation location={location.pathname}>
          <Navigation.Section
            items={filterMenu([
              {
                url: "/",
                label: "Home",
                icon: HomeMajor,
                exactMatch: true,
              },
              {
                url: "/orders",
                label: "Orders",
                icon: OrdersMajor,
                subNavigationItems: [
                  {
                    url: '/orders',
                    label: 'Orders',
                    roles: ['order_read', 'order_edit', 'order_cancel']
                  },
                  {
                    url: '/orders/drafts',
                    label: 'Drafts',
                    roles: ['order_read', 'order_edit', 'order_cancel']
                  },
                  {
                    url: '/orders/checkouts',
                    label: 'Abandoned Checkouts',
                    roles: ['order_read', 'order_edit', 'order_cancel']
                  },
                ]
              },
              {
                url: "/customers",
                label: "Customers",
                icon: CustomersMajor,
                roles: ['customers'],
              },
              {
                url: "/products",
                label: "Products",
                icon: ProductsMajor,
                subNavigationItems: [
                  {
                    url: "/products",
                    label: "Products",
                    roles: ['product_read', 'product_edit', 'product_edit_content']
                  },
                  {
                    url: "/inventory",
                    label: "Inventory",
                    roles: ['inventory_read', 'inventory_edit']
                  },
                  {
                    url: "/transfers",
                    label: "Transfers",
                    roles: ['inventory_read', 'inventory_edit']
                  },
                  {
                    url: "/collections",
                    label: "Collections",
                    roles: ['collection_edit', 'collection_edit_content']
                  },
                ],
              },
            ], auth.user.roles)}
          />
          <Navigation.Section
            fill
            title="Integrations"
            items={filterMenu(
              [
                {
                  url: "/online",
                  label: "Online Store",
                  roles: ['full'],
                  icon: OnlineStoreMajor,
                },
                {
                  url: "/fulfillments",
                  label: "Fulfillments",
                  roles: ['full'],
                  icon: PackageMajor,
                },
                {
                  url: "/shippings",
                  label: "Shippings",
                  roles: ['full'],
                  icon: ShipmentMajor,
                },
              ],
              auth.user.roles
            )}
          />
          <Navigation.Section
            items={filterMenu([
              {
                url: "/settings",
                label: "Settings",
                icon: SettingsMajor,
                roles: ['full'],
              },
            ], auth.user.roles)}
          />
        </Navigation>
      }
    >
      <Switch>
        <Route path="/orders/drafts">
          <Orders status="draft" />
        </Route>
        <Route path="/orders/checkouts">
          <Orders status="cart" />
        </Route>
        <RoleRoute path="/orders/:id" roles={['order_edit']}>
          <Order />
        </RoleRoute>
        <RoleRoute path="/orders" roles={['order_read', 'order_edit', 'order_cancel']}>
          <Orders status="open" />
        </RoleRoute>
        <RoleRoute path="/customers/new" roles={['customer_edit']}>
          <NewCustomer />
        </RoleRoute>
        <RoleRoute path="/customers/:id" roles={['customer_edit']}>
          <Customer />
        </RoleRoute>
        <RoleRoute path="/customers" roles={['customer_read', 'customer_edit']}>
          <Customers />
        </RoleRoute>
        <RoleRoute path="/products/:id" roles={['product_edit', 'product_edit_content']}>
          <Product />
        </RoleRoute>
        <RoleRoute path="/products" roles={['product_read', 'product_edit', 'product_edit_content']}>
          <Products />
        </RoleRoute>
        <RoleRoute path="/inventory" roles={['inventory_read', 'inventory_edit']}>
          <Inventory />
        </RoleRoute>
        <RoleRoute path="/collections/:id" roles={['collection_edit', 'collection_edit_content']}>
          <Collection />
        </RoleRoute>
        <RoleRoute path="/collections" roles={['collection_edit', 'collection_edit_content']}>
          <Collections />
        </RoleRoute>
        <RoleRoute path="/settings/metas/:id" roles={['admin']}>
          <Meta />
        </RoleRoute>
        <RoleRoute path="/settings/metas" roles={['admin']}>
          <Metas />
        </RoleRoute>
        <RoleRoute path="/settings/locations/:id" roles={['full']}>
          <Location />
        </RoleRoute>
        <RoleRoute path="/settings/locations" roles={['full']}>
          <Locations />
        </RoleRoute>
        <RoleRoute path="/settings/payments/:id" roles={['admin']}>
          <Payment />
        </RoleRoute>
        <RoleRoute path="/settings/payments" roles={['admin']}>
          <Payments />
        </RoleRoute>
        <RoleRoute path="/settings/shippers/:id" roles={['admin']}>
          <Shipper />
        </RoleRoute>
        <RoleRoute path="/settings/shippers" roles={['admin']}>
          <Shippers />
        </RoleRoute>
        <RoleRoute path="/settings/channels/:id" roles={['admin']}>
          <Channel />
        </RoleRoute>
        <RoleRoute path="/settings/channels" roles={['admin']}>
          <Channels />
        </RoleRoute>
        <RoleRoute path="/settings" roles={['full']}>
          <Settings />
        </RoleRoute>
        <Route path="/" exact>
          <Home />
        </Route>
        <Route path="*">
          <Error />
        </Route>
      </Switch>
    </Frame>
  );
}

function App() {
  const theme = {
    colors: {
      primary: '#a02116',
      topBar: {
        background: "#fff",
        backgroundLighter: "#F4F6F8",
        backgroundDarker: "#DFE3E8",
        border: "#C4CDD5",
        color: "#212B36",
      },
    },
    logo: {
      width: 124,
      topBarSource: "/images/logo.svg",
      url: "/",
      accessibilityLabel: "brasal.ma",
      // contextualSaveBarSource:
      // 'https://cdn.shopify.com/s/files/1/0446/6937/files/jaded-pixel-logo-gray.svg?6215648040070010999',
    },
  };
  return (
    <ApolloProvider client={client}>
      <ProvideAuth>
        <BrowserRouter>
          <QueryParamProvider ReactRouterRoute={Route}>
            <Helmet titleTemplate="%s - brasal" defaultTitle="Dashboard" />
            <AppProvider linkComponent={Link} i18n={translations} theme={theme}>
              <Switch>
                <Route path="/login">
                  <Login />
                </Route>
                <PrivateRoute>
                  <AppFrame />
                </PrivateRoute>
                <Route path="*">
                  <Error />
                </Route>
              </Switch>
            </AppProvider>
          </QueryParamProvider>
        </BrowserRouter>
      </ProvideAuth>
    </ApolloProvider>
  );
}

export default App;
